import { EndCondition, Equipment, MovementInfoSetting, NumericSetting } from '../backendType';
import { ActivityDefinition } from '../library';
import { CustomActivityVersionDto, Triple } from '../customActivity';

function numericSetting(t: Triple | undefined): NumericSetting | undefined {
  if (!t) {
    return undefined;
  }
  return { default: t.default, min: t.min, max: t.max, step_size: 1 };
}

// Equipment that takes a kg load (so the plan form shows a load input and the patient app fires the
// Actual-Load popup). Keep in lockstep with the backend CustomActivityUtils.WeightBearingEquipment.
const WEIGHT_BEARING_EQUIPMENT = new Set(['Dumbbells', 'Ankle Weights', 'Bar With Weights']);

// Synthesizes the portal Equipment[] from the editor's flat name list + the activity's single
// global `load` triple. The standard library carries per-equipment load metadata; the custom editor
// has one load for the whole activity, so weight-bearing items get Units='kg' + the load triple's
// default/min/max (which makes FormEquipment/EquipmentLoadInput render the Load input via
// isLoadInput = Units && !Options). Distinct Group per item so none are mutually exclusive.
function toEquipmentList(
  names: string[] | undefined,
  load: Triple | undefined,
  groupOffset: number,
): Equipment[] | undefined {
  if (!names || names.length === 0) {
    return undefined;
  }
  return names.map((name, index) => {
    const equipment: Equipment = { Name: name, Group: groupOffset + index };
    if (WEIGHT_BEARING_EQUIPMENT.has(name) && load) {
      equipment.Units = 'kg';
      equipment.Default = String(load.default);
      equipment.Min = load.min;
      equipment.Max = load.max;
    }
    return equipment;
  });
}

// Maps an immutable custom-activity VERSION into the portal's ActivityDefinition shape, so it
// flows through the EXACT same picker → createEmptyActivityInput → toMovementInput path as a
// standard activity. The `id` is the version's movement_id (what the plan item binds to and the
// backend resolves the snapshot from).
//
// `settings` keys are deliberate: only the keys present here are treated as "this activity has
// this field" by createEmptyActivityInput and toMovementInput. We expose the chosen completion
// target + sets/hold/rest/load/rate, mark it an Exercise. Whether we expose `side` is what drives
// fullfilMissingOrganSide: with NO settings.side it stamps side='NA'; WITH settings.side it
// resolves a concrete Left/Right. The convention is PLANE-dependent and mirrors the standard
// library + the backend BuildAlgoEnvelope:
//   - Left/Right                       → side {Left,Right}, app sends Left/Right.
//   - Symmetric on Sagittal (side-facing) → side {Dont Care,Left,Right}: a side-facing camera needs
//     the patient oriented Left/Right, so we MUST expose side (no Sagittal exercise uses NA).
//   - Symmetric on a front/back/top plane → omit side → app sends NA (snapshot options are [NA]).
// automationLevel=0 (CAPTURE) → no TRACK/MEASURE badge.
export function customVersionToActivityDefinition(
  version: CustomActivityVersionDto,
): ActivityDefinition {
  const def = version.definition;
  const target = def.primary_completion_target;

  const settings: MovementInfoSetting = {
    type: { options: ['Exercise'] },
    sets: numericSetting(def.sets),
    hold: numericSetting(def.hold),
    rest: numericSetting(def.rest),
  };
  if (target === 'reps') {
    settings.reps = numericSetting(def.reps);
  }
  if (target === 'duration') {
    settings.duration = numericSetting(def.duration);
  }
  if (target === 'distance') {
    settings.distance = numericSetting(def.distance);
  }
  // Rate is independent of load — it was gated on `def.load` here, which meant a custom activity
  // with a rate and no load never exposed settings.rate, so the plan form never rendered its Rate
  // field. The header above always described these as separate.
  if (def.rate) {
    settings.rate = {
      default: def.rate.default,
      min: def.rate.min,
      max: def.rate.max,
      unit: 'sec',
    };
  }
  // Expose `side` for Left/Right, and for symmetric activities filmed on the side-facing Sagittal
  // plane (the patient must orient Left/Right). Symmetric on any other plane omits it so the app
  // sends 'NA'. See the plane-dependent convention in the header + backend BuildAlgoEnvelope.
  if (def.side === 'Left/Right') {
    settings.side = { options: ['Left', 'Right'] };
  } else if (def.side === 'Symmetric' && def.plane === 'Sagittal') {
    settings.side = { options: ['Dont Care', 'Left', 'Right'] };
  }

  // For a tracked angle activity, expose minimum_to_consider_rep + initial_angle as options
  // settings. Combined with `repOn` (set below), this makes the treatment-plan form render the
  // per-assignment "Repetition Threshold" + "Initial Angle" fields (Form.tsx gates on
  // settings.minimum_to_consider_rep && repOn), exactly like a standard tracked exercise. The
  // options[0] seeds the default; the clinician's per-assignment value then rides through as the
  // top-level movement_info.minimumToConsiderRep the algo override AND the app's yellow min-rep
  // line read.
  if (def.reps_type === 'Angle' && def.rep_on_angle) {
    if (def.minimum_to_consider_rep !== undefined) {
      settings.minimum_to_consider_rep = {
        options: [String(def.minimum_to_consider_rep)],
      };
    }
    if (def.initial_angle !== undefined) {
      settings.initial_angle = { options: [String(def.initial_angle)] };
    }
  }

  return {
    id: version.movement_id,
    name: version.display_name,
    description: def.description ?? '',
    position: def.position ?? '',
    plane: def.plane ?? '',
    bodyParts: def.body_parts ?? [],
    category: def.categories ?? [],
    // Surface equipment so the plan form renders the equipment chips + (for weight-bearing items)
    // the per-equipment Load input. Distinct Group ranges so required/optional never collide.
    requiredEquipment: toEquipmentList(def.equipment_required, def.load, 0),
    optionalEquipment: toEquipmentList(def.equipment_optional, def.load, 100),
    settings,
    // Real automation level from the version's eng_phase: 0 = Text-guided (CAPTURE, no metrics),
    // >0 = tracked (TRACK). This drives the badge AND every metric render gate (PostMetric,
    // OverTimeMetricsGraph, SessionsView all filter automationLevel === 0), so it MUST be truthful.
    automationLevel: version.eng_phase ?? 0,
    end_condition: version.end_condition as EndCondition,
    // Tracked metric metadata: pass through the backend-synthesized objects (absent for
    // Text-guided). These are the SAME objects the algo/app consume — the portal renders from them
    // (PostMetric matches by metric_name; a missing entry would be dropped). rep_on drives the
    // rep-counting display. Not re-derived client-side (single source of truth = cloud catalog).
    realtimeMetric: version.realtime_metric,
    postExerciseMetric: version.post_exercise_metric,
    repOn: version.rep_on,
    // Clinician-uploaded media (absolute TPH media URLs); the preview uses these instead of the
    // CDN movements path (which 404s for a custom id).
    videoUrl: def.video_url,
    thumbnailUrl: def.thumbnail_url,
    gifUrl: def.gif_url,
  };
}
